import { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Eye, Search, Filter, X, Save, AlertTriangle, Users, Calendar, Cpu } from 'lucide-react'
import { apiService } from '../../services/api'
import { toastError } from '../../services/errorUtils'
import toast from 'react-hot-toast'
import AgentForm from './AgentForm'
import type { AgentData, AgentFormData, AgentPhoneNumber } from '../../types/aicall'

interface Agent {
  id: string
  name: string
  description?: string
  status: string
  created_at: string
  updated_at?: string
  agent_data?: AgentData
}

export default function Agents() {
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [sortBy, setSortBy] = useState<'name_asc' | 'name_desc' | 'date_desc' | 'date_asc'>('date_desc')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [formData, setFormData] = useState<AgentFormData>({
    name: '',
    description: '',
    default_language: 'pt-BR',
    additional_languages: [],
    first_message: '',
    first_message_language: 'pt-BR',
    disable_interruptions_first_message: false,
    system_prompt: '',
    llm_provider: 'openai',
    llm_model: 'gpt-4o-mini',
    temperature: 0.7,
    max_tokens: -1,
    tools: {
      end_call: true,
      detect_language: true,
      skip_turn: true,
      transfer_to_agent: false,
      transfer_to_number: false,
      play_keypad_tone: false,
      voicemail_detection: false
    },
    knowledge_base: [],
    custom_tools: [],
    custom_mcp_servers: [],
    workspace_auth_connections: [],
    variables: [],
    timezone: 'America/Sao_Paulo',
    enable_language_overrides: false,
    enable_sentiment_analysis: false,
    enable_conversation_summary: false,
    platform_settings: {
      auth: {
        enable_auth: false,
        allowlist: []
      },
      privacy: {
        record_voice: true,
        retention_days: 730,
        delete_transcript_and_pii: true,
        delete_audio: true
      },
      widget: {
        variant: 'full',
        placement: 'bottom-right',
        expandable: 'never',
        feedback_mode: 'during',
        bg_color: '#ffffff',
        text_color: '#000000',
        btn_color: '#000000',
        btn_text_color: '#ffffff'
      },
      call_limits: {
        daily_limit: 100000,
        agent_concurrency_limit: -1,
        bursting_enabled: true
      }
    },
    tags: []
  })
  const [formLoading, setFormLoading] = useState(false)

  useEffect(() => {
    loadAgents()
  }, [])

  const loadAgents = async () => {
    try {
      setLoading(true)
      const response = await apiService.getAgents()
      setAgents(response.data.data || [])
    } catch (error) {
      toastError(error, 'Erro ao carregar agentes')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateAgent = async () => {
    if (!formData.name.trim()) {
      toast.error('Nome do agente é obrigatório')
      return
    }

    try {
      setFormLoading(true)
      await apiService.createAgent(formData)

      toast.success('Agente criado com sucesso!')
      setShowCreateModal(false)
      resetForm()
      loadAgents()
    } catch (error) {
      toastError(error, 'Erro ao criar agente')
    } finally {
      setFormLoading(false)
    }
  }

  const handleEditAgent = async () => {
    if (!selectedAgent || !formData.name.trim()) {
      toast.error('Nome do agente é obrigatório')
      return
    }

    try {
      setFormLoading(true)
      await apiService.updateAgent(selectedAgent.id, formData)

      toast.success('Agente atualizado com sucesso!')
      setShowEditModal(false)
      setSelectedAgent(null)
      resetForm()
      loadAgents()
    } catch (error) {
      toastError(error, 'Erro ao atualizar agente')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDeleteAgent = async () => {
    if (!selectedAgent) return

    try {
      setFormLoading(true)
      await apiService.deleteAgent(selectedAgent.id)

      toast.success('Agente deletado com sucesso!')
      setShowDeleteModal(false)
      setSelectedAgent(null)
      loadAgents()
    } catch (error) {
      toastError(error, 'Erro ao deletar agente')
    } finally {
      setFormLoading(false)
    }
  }

  const openEditModal = async (agent: Agent) => {
    setSelectedAgent(agent)
    setFormLoading(true)

    try {
      const response = await apiService.getAgent(agent.id)
      const agentData = response.data.data as AgentData & { voice_id?: string; tts_model?: string }

      const detectLanguageFromPrompt = (prompt?: string) => {
        if (!prompt) return 'pt-BR'
        const portugueseWords = ['você', 'é', 'um', 'de', 'para', 'com', 'não', 'que', 'seu', 'sua', 'como', 'mais', 'muito', 'bem', 'também', 'assim', 'então', 'quando', 'onde', 'porque', 'através', 'sobre', 'entre', 'durante', 'após', 'antes', 'depois', 'agora', 'hoje', 'ontem', 'amanhã']
        const hasPortuguese = portugueseWords.some(word => prompt.toLowerCase().includes(word))
        return hasPortuguese ? 'pt-BR' : 'en-US'
      }

      const detectedLanguage = detectLanguageFromPrompt(agentData.system_prompt)

      setFormData({
        name: agentData.name || agent.name,
        description: agentData.description || agent.description || '',
        default_language: agentData.default_language || detectedLanguage,
        additional_languages: agentData.additional_languages || [],
        first_message: agentData.first_message || '',
        first_message_language: agentData.first_message_language || detectedLanguage,
        disable_interruptions_first_message: agentData.disable_interruptions_first_message || false,
        system_prompt: agentData.system_prompt || '',
        llm_provider: agentData.llm_model?.startsWith('gpt') || agentData.llm_model?.startsWith('o1') || agentData.llm_model?.startsWith('o3') ? 'openai' : agentData.llm_model?.startsWith('gemini') ? 'google' : agentData.llm_model?.startsWith('claude') ? 'anthropic' : 'openai',
        llm_model: agentData.llm_model || 'gpt-4o-mini',
        temperature: agentData.temperature ?? 0.7,
        max_tokens: agentData.max_tokens ?? -1,
        tools: {
          end_call: agentData.tools?.end_call ?? true,
          detect_language: agentData.tools?.detect_language ?? true,
          skip_turn: agentData.tools?.skip_turn ?? true,
          transfer_to_agent: agentData.tools?.transfer_to_agent ?? false,
          transfer_to_number: agentData.tools?.transfer_to_number ?? false,
          play_keypad_tone: agentData.tools?.play_keypad_tone ?? false,
          voicemail_detection: agentData.tools?.voicemail_detection ?? false
        },
        knowledge_base: agentData.knowledge_base || [],
        custom_tools: agentData.custom_tools || [],
        custom_mcp_servers: agentData.custom_mcp_servers || [],
        workspace_auth_connections: agentData.platform_settings?.auth?.allowlist?.map((email: string) => ({ email })) || [],
        variables: Array.isArray(agentData.variables) ? agentData.variables : agentData.variables && typeof agentData.variables === 'object' ? Object.keys(agentData.variables).map((key) => ({ key, value: (agentData.variables as Record<string, string>)[key] })) : [],
        timezone: agentData.timezone || 'America/Sao_Paulo',
        enable_language_overrides: agentData.enable_language_overrides || false,
        enable_sentiment_analysis: agentData.enable_sentiment_analysis || false,
        enable_conversation_summary: agentData.enable_conversation_summary || false,
        platform_settings: {
          auth: agentData.platform_settings?.auth || { enable_auth: false, allowlist: [] },
          privacy: agentData.platform_settings?.privacy || { record_voice: true, retention_days: 730, delete_transcript_and_pii: true, delete_audio: true },
          widget: agentData.platform_settings?.widget || { variant: 'full', placement: 'bottom-right', expandable: 'never', feedback_mode: 'during', bg_color: '#ffffff', text_color: '#000000', btn_color: '#000000', btn_text_color: '#ffffff' },
          call_limits: agentData.platform_settings?.call_limits || { daily_limit: 100000, agent_concurrency_limit: -1, bursting_enabled: true }
        },
        tags: Array.isArray(agentData.tags) ? agentData.tags : [],
        voice_id: agentData.voice_id || '',
        tts_model: agentData.tts_model || 'eleven_turbo_v2_5'
      })

      setShowEditModal(true)
    } catch (error) {
      toastError(error, 'Erro ao carregar dados do agente')

      setFormData({
        name: agent.name,
        description: agent.description || '',
        default_language: 'pt-BR',
        additional_languages: [],
        first_message: '',
        first_message_language: 'pt-BR',
        disable_interruptions_first_message: false,
        system_prompt: '',
        llm_provider: 'openai',
        llm_model: 'gpt-4o-mini',
        temperature: 0.7,
        max_tokens: -1,
        tools: {
          end_call: true,
          detect_language: true,
          skip_turn: true,
          transfer_to_agent: false,
          transfer_to_number: false,
          play_keypad_tone: false,
          voicemail_detection: false
        },
        knowledge_base: [],
        custom_tools: [],
        custom_mcp_servers: [],
        workspace_auth_connections: [],
        variables: [],
        timezone: 'America/Sao_Paulo',
        enable_language_overrides: false,
        enable_sentiment_analysis: false,
        enable_conversation_summary: false,
        platform_settings: {
          auth: { enable_auth: false, allowlist: [] },
          privacy: { record_voice: true, retention_days: 730, delete_transcript_and_pii: true, delete_audio: true },
          widget: { variant: 'full', placement: 'bottom-right', expandable: 'never', feedback_mode: 'during', bg_color: '#ffffff', text_color: '#000000', btn_color: '#000000', btn_text_color: '#ffffff' },
          call_limits: { daily_limit: 100000, agent_concurrency_limit: -1, bursting_enabled: true }
        },
        tags: []
      })
      setShowEditModal(true)
    } finally {
      setFormLoading(false)
    }
  }

  const openViewModal = async (agent: Agent) => {
    setSelectedAgent(agent)
    setFormLoading(true)
    try {
      const response = await apiService.getAgent(agent.id)
      const agentData = response.data.data as AgentData & { voice_id?: string; tts_model?: string }
      setSelectedAgent({
        ...agent,
        agent_data: agentData
      })
      setShowViewModal(true)
    } catch (error) {
      toastError(error, 'Erro ao carregar dados do agente')
      setShowViewModal(true)
    } finally {
      setFormLoading(false)
    }
  }

  const openDeleteModal = (agent: Agent) => {
    setSelectedAgent(agent)
    setShowDeleteModal(true)
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      default_language: 'pt-BR',
      additional_languages: [],
      first_message: '',
      first_message_language: 'pt-BR',
      disable_interruptions_first_message: false,
      system_prompt: '',
      llm_provider: 'openai',
      llm_model: 'gpt-4o-mini',
      temperature: 0.7,
      max_tokens: -1,
      tools: {
        end_call: true,
        detect_language: true,
        skip_turn: true,
        transfer_to_agent: false,
        transfer_to_number: false,
        play_keypad_tone: false,
        voicemail_detection: false
      },
      knowledge_base: [],
      custom_tools: [],
      custom_mcp_servers: [],
      workspace_auth_connections: [],
      variables: [],
      timezone: 'America/Sao_Paulo',
      enable_language_overrides: false,
      enable_sentiment_analysis: false,
      enable_conversation_summary: false,
      platform_settings: {
        auth: {
          enable_auth: false,
          allowlist: []
        },
        privacy: {
          record_voice: true,
          retention_days: 730,
          delete_transcript_and_pii: true,
          delete_audio: true
        },
        widget: {
          variant: 'full',
          placement: 'bottom-right',
          expandable: 'never',
          feedback_mode: 'during',
          bg_color: '#ffffff',
          text_color: '#000000',
          btn_color: '#000000',
          btn_text_color: '#ffffff'
        },
        call_limits: {
          daily_limit: 100000,
          agent_concurrency_limit: -1,
          bursting_enabled: true
        }
      },
      tags: []
    })
    setSelectedAgent(null)
  }

  const filteredAgents = agents
    .filter(agent => {
      const q = searchTerm.toLowerCase()
      const matchesSearch = !q || agent.name.toLowerCase().includes(q) || agent.description?.toLowerCase().includes(q)
      const matchesStatus = filterStatus === 'all' || agent.status === filterStatus
      return matchesSearch && matchesStatus
    })
    .sort((a, b) => {
      if (sortBy === 'name_asc') return a.name.localeCompare(b.name, 'pt-BR')
      if (sortBy === 'name_desc') return b.name.localeCompare(a.name, 'pt-BR')
      if (sortBy === 'date_asc') return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    })

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="badge badge-success">Ativo</span>
      case 'inactive':
        return <span className="badge badge-warning">Inativo</span>
      case 'error':
        return <span className="badge badge-danger">Erro</span>
      default:
        return <span className="badge badge-info">{status}</span>
    }
  }

  // Extract LLM model short label from agent_data
  const getLlmModel = (agent: Agent): string => {
    const llm = agent.agent_data?.conversation_config?.agent?.prompt?.llm
      || agent.agent_data?.llm_model
    return llm || 'gpt-4o-mini'
  }

  // Count phone numbers
  const getPhoneCount = (agent: Agent): number => {
    return agent.agent_data?.phone_numbers?.length ?? 0
  }

  if (loading) {
    return (
      <div className="page-shell">
        <div className="page-header">
          <div>
            <h1 className="page-title">Agentes</h1>
            <p className="page-description">Gerencie agentes de voz, prompts, ferramentas e limites de atendimento.</p>
          </div>
        </div>
        {/* Loading skeleton */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="card animate-pulse">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-slate-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 rounded bg-slate-200" />
                  <div className="h-3 w-1/2 rounded bg-slate-200" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-3 w-full rounded bg-slate-200" />
                <div className="h-3 w-2/3 rounded bg-slate-200" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <h1 className="page-title">Agentes</h1>
          <p className="page-description">
            Gerencie agentes de voz, prompts, ferramentas e limites de atendimento.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4" />
          Novo Agente
        </button>
      </div>

      <div className="toolbar">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            aria-label="Buscar agentes"
            placeholder="Buscar agentes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>
        <button
          className={`btn-secondary ${showFilters ? 'bg-primary-50 text-primary-700 border-primary-200' : ''}`}
          onClick={() => setShowFilters(v => !v)}
        >
          <Filter className="h-4 w-4" />
          Filtros
        </button>
      </div>

      {showFilters && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-slate-600">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input h-8 py-0 text-sm"
            >
              <option value="all">Todos</option>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-slate-600">Ordenar por</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="input h-8 py-0 text-sm"
            >
              <option value="date_desc">Mais recentes</option>
              <option value="date_asc">Mais antigos</option>
              <option value="name_asc">Nome A→Z</option>
              <option value="name_desc">Nome Z→A</option>
            </select>
          </div>
          {(filterStatus !== 'all' || sortBy !== 'date_desc') && (
            <button
              className="text-xs font-semibold text-slate-500 hover:text-slate-700"
              onClick={() => { setFilterStatus('all'); setSortBy('date_desc') }}
            >
              <X className="inline h-3 w-3 mr-1" />Limpar
            </button>
          )}
        </div>
      )}

      {filteredAgents.length === 0 ? (
        <div className="empty-state">
          <Users className="mb-4 h-12 w-12 text-slate-300" />
          <h3 className="text-sm font-semibold text-slate-950">
            {searchTerm ? 'Nenhum agente encontrado' : 'Nenhum agente criado'}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {searchTerm
              ? 'Tente ajustar os filtros de busca.'
              : 'Crie seu primeiro agente para começar a fazer ligações com IA.'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary mt-4"
            >
              <Plus className="h-4 w-4" />
              Criar Agente
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filteredAgents.map((agent) => (
            <article
              key={agent.id}
              className="card group hover:border-primary-200 hover:shadow-md transition-all cursor-pointer"
              onClick={() => openViewModal(agent)}
            >
              {/* Card header: avatar + name + id */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 bg-primary-100 text-primary-700 font-bold text-lg rounded-full h-10 w-10 flex items-center justify-center uppercase select-none">
                  {agent.name.charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-slate-950 truncate">{agent.name}</h3>
                  <p className="text-xs font-mono text-slate-400 truncate mt-0.5">{agent.id}</p>
                </div>
                {/* Status badge */}
                <div className="flex-shrink-0">{getStatusBadge(agent.status)}</div>
              </div>

              {/* Description */}
              {agent.description && (
                <p className="mt-3 text-sm text-slate-500 line-clamp-2">{agent.description}</p>
              )}

              {/* Meta row: LLM badge + phone count + date */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="badge badge-info flex items-center gap-1">
                  <Cpu className="h-3 w-3" />
                  {getLlmModel(agent)}
                </span>
                <span className="badge">
                  {getPhoneCount(agent)} número{getPhoneCount(agent) !== 1 ? 's' : ''}
                </span>
                <span className="ml-auto text-xs text-slate-500 flex items-center gap-1 tabular-nums">
                  <Calendar className="h-3 w-3" />
                  {new Date(agent.created_at).toLocaleDateString('pt-BR')}
                </span>
              </div>

              {/* Hover actions */}
              <div className="mt-4 flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => { e.stopPropagation(); openViewModal(agent) }}
                  className="btn-icon text-primary-600"
                  aria-label={`Visualizar agente ${agent.name}`}
                >
                  <Eye className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); openEditModal(agent) }}
                  className="btn-icon text-warning-600"
                  aria-label={`Editar agente ${agent.name}`}
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); openDeleteModal(agent) }}
                  className="btn-icon text-danger-600"
                  aria-label={`Deletar agente ${agent.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Modal de criação */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          <div className="modal-backdrop" onClick={() => { setShowCreateModal(false); resetForm(); }} />
          <div className="modal-panel relative w-full max-w-4xl flex flex-col max-h-[92vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
              <h3 className="text-lg font-semibold text-slate-950">Criar Novo Agente</h3>
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="btn-icon text-slate-500"
                aria-label="Fechar modal de criação"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Scrollable body */}
            <div className={`flex-1 overflow-y-auto px-6 pt-0 pb-5 min-h-0 ${formLoading ? 'opacity-50 pointer-events-none' : ''}`}>
              <AgentForm
                formData={formData}
                setFormData={setFormData}
                isEdit={false}
              />
            </div>
            {/* Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-0 px-6 pb-4 flex-shrink-0 bg-slate-50">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                disabled={formLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleCreateAgent}
                disabled={formLoading}
              >
                {formLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {formLoading ? 'Criando...' : 'Criar Agente'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edição */}
      {showEditModal && selectedAgent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          <div className="modal-backdrop" onClick={() => { setShowEditModal(false); resetForm(); }} />
          <div className="modal-panel relative w-full max-w-4xl flex flex-col max-h-[92vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="bg-primary-100 text-primary-700 font-bold text-lg rounded-full h-9 w-9 flex items-center justify-center uppercase select-none flex-shrink-0">
                  {selectedAgent.name.charAt(0)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-950">Editar Agente</h3>
                  <p className="text-xs text-slate-400 font-mono truncate max-w-[280px]">{selectedAgent.id}</p>
                </div>
              </div>
              <button
                onClick={() => { setShowEditModal(false); resetForm(); }}
                className="btn-icon text-slate-500"
                aria-label="Fechar modal de edição"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Scrollable body */}
            <div className={`flex-1 overflow-y-auto px-6 pt-0 pb-5 min-h-0 ${formLoading ? 'opacity-50 pointer-events-none' : ''}`}>
              <AgentForm
                formData={formData}
                setFormData={setFormData}
                isEdit={true}
              />
            </div>
            {/* Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-0 px-6 pb-4 flex-shrink-0 bg-slate-50">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => { setShowEditModal(false); resetForm(); }}
                disabled={formLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleEditAgent}
                disabled={formLoading}
              >
                {formLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {formLoading ? 'Salvando...' : 'Salvar alterações'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de visualização */}
      {showViewModal && selectedAgent && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="modal-backdrop" onClick={() => { setShowViewModal(false); setSelectedAgent(null); }} />
            <div className="modal-panel relative inline-block text-left max-w-2xl w-full sm:my-8 sm:align-middle overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="bg-primary-100 text-primary-700 font-bold text-lg rounded-full h-10 w-10 flex items-center justify-center uppercase select-none flex-shrink-0">
                    {selectedAgent.name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-slate-950 truncate">{selectedAgent.name}</h3>
                    <div className="mt-0.5">{getStatusBadge(selectedAgent.status)}</div>
                  </div>
                </div>
                <button
                  onClick={() => { setShowViewModal(false); setSelectedAgent(null); }}
                  className="btn-icon text-slate-500 flex-shrink-0 ml-3"
                  aria-label="Fechar detalhes do agente"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-5 max-h-[65vh] overflow-y-auto">
                {/* ID & basic info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">ID</label>
                    <p className="text-sm text-slate-900 bg-slate-50 px-3 py-2 rounded-lg font-mono truncate">{selectedAgent.id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Criado em</label>
                    <p className="text-sm text-slate-900 bg-slate-50 px-3 py-2 rounded-lg tabular-nums">
                      {new Date(selectedAgent.created_at).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>

                {selectedAgent.description && (
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Descrição</label>
                    <p className="text-sm text-slate-700 bg-slate-50 px-3 py-2 rounded-lg min-h-[48px]">
                      {selectedAgent.description}
                    </p>
                  </div>
                )}

                {selectedAgent.agent_data && (
                  <>
                    {/* LLM & config */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-slate-50 rounded-lg px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Modelo LLM</p>
                        <p className="mt-1 text-sm font-semibold text-slate-950 truncate">
                          {selectedAgent.agent_data.llm_model || 'N/A'}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-lg px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Idioma</p>
                        <p className="mt-1 text-sm font-semibold text-slate-950 truncate">
                          {selectedAgent.agent_data.default_language || 'pt-br'}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-lg px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Temperatura</p>
                        <p className="mt-1 text-sm font-semibold text-slate-950 tabular-nums">
                          {selectedAgent.agent_data.temperature ?? 0.7}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-lg px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Max Tokens</p>
                        <p className="mt-1 text-sm font-semibold text-slate-950 tabular-nums">
                          {selectedAgent.agent_data.max_tokens ?? -1}
                        </p>
                      </div>
                    </div>

                    {/* First message */}
                    {selectedAgent.agent_data.first_message && (
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">Primeira Mensagem</label>
                        <p className="text-sm text-slate-700 bg-slate-50 px-3 py-2 rounded-lg min-h-[48px]">
                          {selectedAgent.agent_data.first_message}
                        </p>
                      </div>
                    )}

                    {/* System prompt */}
                    {selectedAgent.agent_data.system_prompt && (
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">System Prompt</label>
                        <p className="text-sm text-slate-700 bg-slate-50 px-3 py-2 rounded-lg min-h-[80px] max-h-[160px] overflow-y-auto whitespace-pre-wrap">
                          {selectedAgent.agent_data.system_prompt}
                        </p>
                      </div>
                    )}

                    {/* Active tools */}
                    {selectedAgent.agent_data.tools && (
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">Ferramentas Ativas</label>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(selectedAgent.agent_data.tools)
                            .filter(([, enabled]) => enabled)
                            .map(([tool]) => (
                              <span key={tool} className="badge badge-success">
                                {tool.replace(/_/g, ' ')}
                              </span>
                            ))}
                          {Object.entries(selectedAgent.agent_data.tools).every(([, enabled]) => !enabled) && (
                            <span className="text-sm text-slate-500">Nenhuma ferramenta ativa</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Phone numbers */}
                    {selectedAgent.agent_data.phone_numbers && selectedAgent.agent_data.phone_numbers.length > 0 && (
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">
                          Números de telefone ({selectedAgent.agent_data.phone_numbers.length})
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {selectedAgent.agent_data.phone_numbers.map((phone: AgentPhoneNumber) => (
                            <div
                              key={phone.phone_number_id || phone.phone_number}
                              className="bg-slate-100 rounded-full px-3 py-1 text-sm font-mono flex items-center gap-2"
                            >
                              <span className="truncate max-w-[180px]">{phone.phone_number}</span>
                              {phone.supports_inbound && (
                                <span className="badge badge-success text-xs">In</span>
                              )}
                              {phone.supports_outbound && (
                                <span className="badge badge-info text-xs">Out</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Additional languages */}
                    {selectedAgent.agent_data.additional_languages && selectedAgent.agent_data.additional_languages.length > 0 && (
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">Idiomas Adicionais</label>
                        <div className="flex flex-wrap gap-2">
                          {selectedAgent.agent_data.additional_languages.map((lang: string) => (
                            <span key={lang} className="badge badge-info">{lang}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tags */}
                    {selectedAgent.agent_data.tags && selectedAgent.agent_data.tags.length > 0 && (
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">Tags</label>
                        <div className="flex flex-wrap gap-2">
                          {selectedAgent.agent_data.tags.map((tag: string) => (
                            <span key={tag} className="badge">{tag}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {selectedAgent.updated_at && (
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Atualizado em</label>
                    <p className="text-sm text-slate-900 bg-slate-50 px-3 py-2 rounded-lg tabular-nums">
                      {new Date(selectedAgent.updated_at).toLocaleString('pt-BR')}
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setShowViewModal(false); setSelectedAgent(null); }}
                >
                  Fechar
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => { setShowViewModal(false); openEditModal(selectedAgent); }}
                >
                  <Edit className="h-4 w-4" />
                  Editar Agente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de exclusão */}
      {showDeleteModal && selectedAgent && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="modal-backdrop" onClick={() => { setShowDeleteModal(false); setSelectedAgent(null); }} />
            <div className="modal-panel relative inline-block text-left max-w-md w-full sm:my-8 sm:align-middle">
              <div className="px-6 py-5">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-danger-100">
                    <AlertTriangle className="h-5 w-5 text-danger-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-950">Confirmar exclusão</h3>
                    <p className="mt-2 text-sm text-slate-500">
                      Tem certeza que deseja deletar o agente <strong className="text-slate-700">"{selectedAgent.name}"</strong>?
                      Esta ação não pode ser desfeita.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setShowDeleteModal(false); setSelectedAgent(null); }}
                  disabled={formLoading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={handleDeleteAgent}
                  disabled={formLoading}
                >
                  {formLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  {formLoading ? 'Deletando...' : 'Deletar Agente'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
